import { isCancelled, isCompletedShipment } from './cancellation';
import { normalizePlate } from './serialNumbers';

export const MIN_EMPTY_KM_SAMPLES = 3;
const reading = value => {
  if (value == null || String(value).trim() === '') return null;
  const number = Number(String(value).replace(',', '.'));
  return Number.isFinite(number) && number >= 0 ? number : null;
};
const stamp = (date, time) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date || '')) return null;
  const clock = time || '00:00';
  if (!/^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/.test(clock)) return null;
  const value = Date.parse(`${date}T${clock.length === 5 ? `${clock}:00` : clock}Z`);
  return Number.isFinite(value) ? value : null;
};
// First index for which predicate is false (the lists are chronologically sorted).
const boundary = (rows, predicate) => { let lo = 0, hi = rows.length; while (lo < hi) { const mid = (lo + hi) >>> 1; if (predicate(rows[mid])) lo = mid + 1; else hi = mid; } return lo; };

export function buildEmptyKmAnalysis(records) {
  const entries = records.filter(record => !isCancelled(record) && record.kaynakTipi !== "HARICI" && normalizePlate(record.plaka)).map(record => ({ record, plate: normalizePlate(record.plaka), load: stamp(record.dolumTarihi, record.dolumSaati), end: stamp(record.dokumTarihi, record.dokumSaati) }));
  const completed = new Map(), pending = new Map();
  for (const entry of entries) {
    if (entry.load == null) continue;
    if (isCompletedShipment(entry.record) && entry.end != null && entry.end >= entry.load) {
      if (!completed.has(entry.plate)) completed.set(entry.plate, []);
      completed.get(entry.plate).push(entry);
    } else if (entry.record.durum === 'DOKUM_BEKLIYOR') {
      if (!pending.has(entry.plate)) pending.set(entry.plate, []);
      pending.get(entry.plate).push(entry);
    }
  }
  completed.forEach(rows => rows.sort((a, b) => a.end - b.end || a.load - b.load));
  pending.forEach(rows => rows.sort((a, b) => a.load - b.load));
  const result = new Map(), routes = new Map();
  for (const entry of entries) {
    const { record, load, plate } = entry;
    const analysis = { previous: null, emptyKm: null, averageKm: null, limitKm: null, sampleCount: 0, abnormal: false, warning: '', routeKey: '', routeLabel: '' };
    result.set(record.id, analysis);
    if (load == null) continue;
    const history = completed.get(plate) || [];
    let index = boundary(history, item => item.end <= load) - 1;
    while (index >= 0 && (history[index].record.id === record.id || history[index].load >= load)) index--;
    const previous = history[index];
    if (!previous) continue;
    const unfinished = pending.get(plate) || [];
    const pendingIndex = boundary(unfinished, item => item.load < load) - 1;
    if (pendingIndex >= 0 && unfinished[pendingIndex].load >= previous.end) continue;
    analysis.previous = previous.record;
    analysis.routeLabel = [previous.record.mtaAdi, record.fabrikaAdi].filter(Boolean).join(' → ');
    const exit = reading(record.dolumKm), arrival = reading(previous.record.dokumKm);
    if (exit == null || arrival == null) continue;
    analysis.emptyKm = exit - arrival;
    if (analysis.emptyKm < 0) { analysis.abnormal = true; analysis.warning = 'Boş KM negatif; çıkış ve önceki döküm kilometresini kontrol edin.'; continue; }
    if (!previous.record.mtaId || !record.fabrikaId) continue;
    analysis.routeKey = JSON.stringify([previous.record.mtaId, record.fabrikaId]);
    if (isCompletedShipment(record) && entry.end != null && entry.end >= load) {
      if (!routes.has(analysis.routeKey)) routes.set(analysis.routeKey, []);
      routes.get(analysis.routeKey).push({ end: entry.end, km: analysis.emptyKm });
    }
  }
  routes.forEach(rows => {
    rows.sort((a, b) => a.end - b.end);
    let sum = 0;
    rows.forEach(item => { sum += item.km; item.sum = sum; });
  });
  for (const entry of entries) {
    const analysis = result.get(entry.record.id);
    if (!analysis.routeKey || analysis.abnormal) continue;
    const samples = routes.get(analysis.routeKey) || [];
    // Only completed historical returns before this loading; never the current/future trip.
    const count = boundary(samples, item => item.end < entry.load);
    analysis.sampleCount = count;
    if (count < MIN_EMPTY_KM_SAMPLES) continue;
    analysis.averageKm = samples[count - 1].sum / count;
    analysis.limitKm = analysis.averageKm * 1.1;
    analysis.abnormal = analysis.emptyKm > analysis.limitKm + 1e-9;
    if (analysis.abnormal) analysis.warning = 'Boş KM, rota ortalamasının %10 üzerinde.';
  }
  return result;
}
